import React, { useEffect, useRef, useState } from "react";
import { SvgIcon, Tooltip } from "@mui/material";

import DSTLVideo from "../../../assets/videos/DSTL-Video.mp4";
import captions from "../../../assets/captions/WTUFO-Intro.en-GB.vtt";
import IntroVideoControl, {
  restart,
  next,
} from "../IntroVideoControl/IntroVideoControl";

import "./LiveCaptionOverlay.scss";

export default function IntroVideoWithBriefingCaptions({
  finishIntro = () => {},
}) {
  const videoRef = useRef(null);
  const rafIdRef = useRef(null);
  const activeCueRef = useRef(null);
  const replayBtnRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [hasEnded, setHasEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [displayText, setDisplayText] = useState("");
  const [hudVisible, setHudVisible] = useState(false);
  const [isShowSubtitles, setIsShowSubtitles] = useState(true);

  const cleanText = (t = "") => t.replace(/<[^>]+>/g, "");

  const handleRestart = () => {
    const video = videoRef.current;
    if (video) {
      // Clear countdown timer
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }

      // reset state
      setDisplayText("");
      setHudVisible(false);
      activeCueRef.current = null;
      setHasEnded(false);
      setCountdown(5);

      video.currentTime = 0;
      video.play?.();
    }
  };

  const animate = () => {
    const video = videoRef.current;
    const cue = activeCueRef.current;

    if (video && cue) {
      const start = cue.startTime;
      const end = cue.endTime;
      const now = video.currentTime;
      const full = cleanText(cue.text) || "";

      const duration = Math.max(0.001, end - start);
      const progress = Math.min(1, Math.max(0, (now - start) / duration));
      const charsToShow = Math.ceil(progress * full.length);

      setDisplayText(full.slice(0, charsToShow));
      setHudVisible(progress > 0 && now <= end);
    } else {
      setHudVisible(false);
    }

    rafIdRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const setupTracks = () => {
      const tracks = video.textTracks;
      if (!tracks || !tracks[0]) return;
      const track = tracks[0];
      track.mode = "hidden";

      track.oncuechange = () => {
        const cue = track.activeCues && track.activeCues[0];
        if (cue) {
          activeCueRef.current = cue;
          setDisplayText("");
          setHudVisible(true);
        } else {
          activeCueRef.current = null;
          setHudVisible(false);
        }
      };
    };

    const onLoadedMeta = () => setupTracks();

    const onPlay = () => {
      setIsPlaying(true);
      setHasEnded(false);
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(animate);
    };

    const onPause = () => {
      setIsPlaying(false);
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(animate);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setHasEnded(true);
      setHudVisible(false);
      cancelAnimationFrame(rafIdRef.current);

      // Start countdown timer
      setCountdown(5);
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            finishIntro();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    const onSeeked = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(animate);
    };

    video.addEventListener("loadedmetadata", onLoadedMeta);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("seeked", onSeeked);

    if (video.readyState >= 1) setupTracks();
    rafIdRef.current = requestAnimationFrame(animate);

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMeta);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("seeked", onSeeked);
      cancelAnimationFrame(rafIdRef.current);

      // Clean up countdown timer
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus Replay on end for accessibility
  useEffect(() => {
    if (hasEnded) {
      replayBtnRef.current?.focus();
    }
  }, [hasEnded]);

  return (
    <div className="intro-video-wrap">
      <video
        playsInline
        muted={isMuted}
        ref={videoRef}
        preload="auto"
        controls={false}
        autoPlay={true}
        className="intro-video"
      >
        <source src={DSTLVideo} type="video/mp4" />
        <track
          default
          kind="subtitles"
          srcLang="en-GB"
          label="English (UK)"
          src={captions}
        />
      </video>

      {/* Hide the corner controls when end overlay is visible */}
      {!hasEnded && (
        <IntroVideoControl
          isMuted={isMuted}
          isPlaying={isPlaying}
          handleSkip={finishIntro}
          handleRestart={handleRestart}
          isShowSubtitles={isShowSubtitles}
          handleToggleMute={() => setIsMuted(!isMuted)}
          handleToggleSubtitles={() => setIsShowSubtitles(!isShowSubtitles)}
        />
      )}

      {/* HUD subtitles */}
      {isShowSubtitles && (
        <div className={`hud-overlay ${hudVisible ? "is-visible" : ""}`}>
          <div className="hud-caption">
            <span className="hud-prefix">[BRIEFING]</span>
            <span className="hud-text">{displayText}</span>
            <span className="hud-cursor" />
          </div>
        </div>
      )}

      {/* END OVERLAY */}
      {hasEnded && (
        <div
          className="end-overlay"
          role="dialog"
          aria-label="Briefing complete"
          aria-modal="true"
        >
          <div className="end-card">
            {/* Countdown message */}
            <div className="end-countdown">
              Moving to next screen in {countdown} second
              {countdown !== 1 ? "s" : ""}
            </div>

            <div className="end-actions">
              <Tooltip
                title="Restart"
                slotProps={{ tooltip: { className: "end-button-tooltip" } }}
              >
                <div
                  ref={replayBtnRef}
                  className="end-btn"
                  onClick={handleRestart}
                >
                  <SvgIcon component={restart} inheritViewBox />
                </div>
              </Tooltip>
              <div className="end-btn end-btn-primary" onClick={finishIntro}>
                <Tooltip
                  title="Next"
                  slotProps={{ tooltip: { className: "end-button-tooltip" } }}
                >
                  <SvgIcon component={next} inheritViewBox />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
